// One rule for keeping the device's and the account's onboarding in step,
// run once per session after the first sync.

import { ONBOARDING_VERSION } from "@aybuben/core/onboarding/version.ts";
import type { PrefPatch } from "@aybuben/core/progress/event.ts";
import type { GameState } from "@aybuben/core/state.ts";

import type { DevicePrefs } from "../device/prefs.ts";

export interface Reconciled {
	/** Record on the account, or null. */
	patch: PrefPatch | null;
	/** Write to the device, or null. */
	device: DevicePrefs | null;
	/** Show the onboarding as a tour to this signed-in account. */
	tour: boolean;
}

export function reconcile(
	device: DevicePrefs,
	account: Pick<GameState, "onboarded" | "lang" | "done">,
	version: number = ONBOARDING_VERSION,
): Reconciled {
	if (device.onboarded > account.onboarded) {
		// Onboarding happened on this device before the account had it: a new
		// guest, a new account, or a sign-in right after onboarding. A language
		// chosen there belongs to an account with nothing in it yet; an account
		// with progress keeps its own, and the device follows it.
		const fresh = Object.keys(account.done).length === 0;
		const tour = device.onboarded < version;
		if (fresh)
			return { patch: { onboarded: device.onboarded, lang: device.lang }, device: null, tour };
		return {
			patch: { onboarded: device.onboarded },
			device:
				device.lang === account.lang ? null : { lang: account.lang, onboarded: device.onboarded },
			tour,
		};
	}
	const same = device.lang === account.lang && device.onboarded === account.onboarded;
	return {
		patch: null,
		// The signed-out screens on this device should speak the account's language next time.
		device: same ? null : { lang: account.lang, onboarded: account.onboarded },
		tour: account.onboarded < version,
	};
}
